<template>
  <div class="venta-carrito-wrapper">
    <!-- Selector de miembro (opcional) -->
    <div v-if="mostrarSelectorMiembro" class="field">
      <label class="field-label">Miembro (opcional)</label>
      <v-autocomplete
        v-model="idMiembroSeleccionado"
        :items="miembros"
        item-text="nombre"
        item-value="id"
        clearable
        outlined
        dense
        hide-details="auto"
        placeholder="Selecciona un miembro"
        no-data-text="No se encontraron miembros"
        class="dark-input"
      ></v-autocomplete>
    </div>

    <!-- Buscador para agregar productos -->
    <div v-if="mostrarBuscadorProducto" class="field" :class="{ 'mt-lg': mostrarSelectorMiembro }">
      <label class="field-label">Producto</label>
      <v-autocomplete
        v-model="productoSeleccionado"
        :items="productosDisponibles"
        item-text="nombre"
        item-value="id"
        return-object
        clearable
        outlined
        dense
        hide-details="auto"
        placeholder="Busca un producto"
        no-data-text="No hay productos con stock disponible"
        class="dark-input"
        @change="agregarProducto"
      >
        <template v-slot:item="{ item }">
          <div class="autocomplete-item">
            <span class="autocomplete-item-title">{{ item.nombre }}</span>
            <span class="autocomplete-item-sub">${{ item.precio }} · Stock {{ item.stock }}</span>
          </div>
        </template>
      </v-autocomplete>
    </div>

    <!-- Tabla del carrito -->
    <div class="table-wrapper" :class="{ 'mt-lg': mostrarSelectorMiembro || mostrarBuscadorProducto }">
      <v-data-table
        :headers="encabezadoProductos"
        :items="productos"
        hide-default-footer
        no-data-text="Aún no has agregado productos"
        class="dash-table"
      >
        <template v-slot:item.nombre="{ item }">
          <span class="cell-strong">{{ item.nombre }}</span>
        </template>

        <template v-slot:item.precio="{ item }">
          <span class="cell-secondary">${{ item.precio }}</span>
        </template>

        <template v-slot:item.cantidad="{ item }">
          <v-text-field
            v-model.number="item.cantidad"
            type="number"
            min="1"
            :max="item.stock"
            dense
            outlined
            hide-details
            class="dark-input qty-input"
            @input="calcularTotal"
          ></v-text-field>
        </template>

        <template v-slot:item.subtotal="{ item }">
          <span class="cell-price">${{ formatearMonto(item.subtotal) }}</span>
        </template>

        <template v-slot:item.acciones="{ item }">
          <button class="icon-btn icon-btn-danger" @click="eliminarProducto(item)">
            <v-icon size="15" color="#FF6B6B">mdi-delete</v-icon>
          </button>
        </template>
      </v-data-table>
    </div>

    <!-- Total -->
    <div class="total-row">
      <span class="total-label">Total</span>
      <span class="total-value">${{ total.toFixed(2) }}</span>
    </div>

    <!-- Acción de registro -->
    <div class="venta-actions">
      <button class="btn-primary" :disabled="!ventaValida || cargando" @click="registrarVenta">
        <span v-if="!cargando">
          <v-icon size="15" class="mr-2">mdi-check</v-icon>
          Registrar venta
        </span>
        <span v-else class="loading-inline">
          <span class="spinner-sm"></span>
          Procesando
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import HttpService from "../../Servicios/HttpService";

export default {
  name: "VentaCarrito",

  props: {
    // Productos con stock disponible, usados por el buscador interno.
    productosDisponibles: {
      type: Array,
      default: () => []
    },
    // Lista de miembros, solo se usa si mostrarSelectorMiembro es true.
    miembros: {
      type: Array,
      default: () => []
    },
    // Muestra el selector de miembro (útil en el flujo de "Nueva venta").
    mostrarSelectorMiembro: {
      type: Boolean,
      default: false
    },
    // Muestra el buscador de productos interno. Ponlo en false si el
    // producto se agrega desde afuera (ej. un botón "+" en una tabla)
    // llamando a agregarProducto() a través de una ref.
    mostrarBuscadorProducto: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    productoSeleccionado: null,
    idMiembroSeleccionado: null,
    productos: [],
    total: 0,
    cargando: false,
    encabezadoProductos: [
      { text: "Producto", value: "nombre" },
      { text: "Precio unitario", value: "precio" },
      { text: "Cantidad", value: "cantidad" },
      { text: "Subtotal", value: "subtotal" },
      { text: "Acciones", value: "acciones", sortable: false }
    ],
  }),

  computed: {
    ventaValida() {
      return this.productos.length > 0 && this.total > 0;
    }
  },

  methods: {
    formatearMonto(valor) {
      const numero = Number(valor);
      return isNaN(numero) ? '0.00' : numero.toFixed(2);
    },

    // Método público: se puede llamar tanto desde el buscador interno
    // como desde el padre a través de una ref (ej. botón "agregar al
    // carrito" en una fila de tabla de productos).
    agregarProducto(producto) {
      if (!producto) return;

      const index = this.productos.findIndex(p => p.id === producto.id);

      if (index >= 0) {
        if (this.productos[index].cantidad < producto.stock) {
          this.productos[index].cantidad += 1;
        } else {
          this.$emit('mensaje', {
            texto: `No hay más stock disponible de "${producto.nombre}"`,
            color: 'warning'
          });
        }
      } else {
        this.productos.push({
          id: producto.id,
          nombre: producto.nombre,
          precio: producto.precio,
          cantidad: 1,
          stock: producto.stock,
          subtotal: producto.precio
        });
      }

      this.calcularTotal();
      this.productoSeleccionado = null;
    },

    eliminarProducto(producto) {
      this.productos = this.productos.filter(p => p.id !== producto.id);
      this.calcularTotal();
    },

    calcularTotal() {
      this.total = this.productos.reduce((total, producto) => {
        producto.subtotal = producto.precio * producto.cantidad;
        return total + producto.subtotal;
      }, 0);
      this.emitirEstado();
    },

    // Notifica al padre el estado actual del carrito. Los refs no son
    // reactivos en Vue, así que si el padre necesita saber cuántos items
    // hay (ej. para un contador en un botón "Ver carrito"), debe
    // escuchar este evento en vez de leer this.$refs.carrito.productos.
    emitirEstado() {
      this.$emit('actualizado', {
        cantidad: this.productos.length,
        total: this.total
      });
    },

    async registrarVenta() {
      if (!this.ventaValida) return;

      this.cargando = true;

      const payload = {
        metodo: "post",
        venta: {
          id_miembro: this.mostrarSelectorMiembro ? this.idMiembroSeleccionado : null,
          productos: this.productos.map(p => ({
            id: p.id,
            cantidad: p.cantidad,
            subtotal: p.subtotal
          })),
          total: this.total
        }
      };

      try {
        const resultado = await HttpService.registrar(payload, "ventas.php");

        if (resultado) {
          this.$emit('mensaje', {
            texto: '✅ Venta registrada correctamente',
            color: 'success'
          });
          this.$emit('venta-registrada', resultado);
          this.reiniciar();
        }
      } catch (error) {
        console.error('Error al registrar venta:', error);
        this.$emit('mensaje', {
          texto: '❌ Error al registrar la venta',
          color: 'error'
        });
      } finally {
        this.cargando = false;
      }
    },

    reiniciar() {
      this.productos = [];
      this.total = 0;
      this.productoSeleccionado = null;
      this.idMiembroSeleccionado = null;
      this.emitirEstado();
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS ===== */
.venta-carrito-wrapper {
  --bg: #0E0E10;
  --card: #17171A;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;

  font-family: var(--font-body);
  color: var(--text-primary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.autocomplete-item {
  display: flex;
  flex-direction: column;
  padding: 6px 0;
}

.autocomplete-item-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.autocomplete-item-sub {
  font-size: 11.5px;
  color: var(--text-tertiary);
  margin-top: 2px;
}

/* ===== TABLE ===== */
.table-wrapper {
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.cell-strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.cell-secondary {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.cell-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
}

.qty-input {
  max-width: 90px;
}

.qty-input >>> .v-input__slot {
  min-height: 34px !important;
}

.icon-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius);
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover {
  border-color: var(--card-border);
  background: rgba(255, 255, 255, 0.03);
}

.icon-btn-danger:hover {
  border-color: rgba(255, 107, 107, 0.3);
  background: rgba(255, 107, 107, 0.06);
}

/* ===== TOTAL ===== */
.total-row {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 10px;
  padding: 16px 4px 4px;
}

.total-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.total-value {
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: 700;
  color: var(--accent);
}

/* ===== ACTIONS ===== */
.venta-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: default;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 11px;
  height: 11px;
  border: 2px solid rgba(14, 14, 16, 0.3);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== Overrides Vuetify (v-autocomplete, v-text-field) ===== */
.dark-input >>> .v-input__slot {
  background: var(--bg) !important;
  border-radius: 4px !important;
  box-shadow: none !important;
  min-height: 42px !important;
}

.dark-input >>> fieldset {
  border-color: var(--card-border) !important;
  border-width: 1px !important;
  border-radius: 4px !important;
  transition: border-color 0.15s ease;
}

.dark-input >>> .v-input--is-focused fieldset {
  border-color: var(--accent) !important;
  border-width: 1.5px !important;
}

.dark-input >>> input,
.dark-input >>> .v-select__selection {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
  font-size: 14px !important;
  caret-color: var(--accent) !important;
}

.dark-input >>> input::placeholder {
  color: var(--text-tertiary) !important;
  opacity: 1 !important;
}

.dark-input >>> .v-icon {
  color: var(--text-tertiary) !important;
}

.dark-input >>> .v-messages {
  color: var(--error) !important;
}

/* Menú del autocomplete (Vuetify lo monta fuera del árbol del componente) */
.venta-carrito-wrapper >>> .v-menu__content {
  background: var(--card) !important;
  border: 1px solid var(--card-border) !important;
  border-radius: 4px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}

.venta-carrito-wrapper >>> .v-list {
  background: transparent !important;
  padding: 4px !important;
}

.venta-carrito-wrapper >>> .v-list-item {
  border-radius: 4px !important;
}

.venta-carrito-wrapper >>> .v-list-item__title,
.venta-carrito-wrapper >>> .v-list-item__subtitle {
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
}

.venta-carrito-wrapper >>> .v-list-item__subtitle {
  color: var(--text-tertiary) !important;
  font-size: 11.5px !important;
}

.venta-carrito-wrapper >>> .v-list-item:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.venta-carrito-wrapper >>> .v-list-item--active {
  background: var(--accent-dim) !important;
}

/* ===== Overrides v-data-table ===== */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
  padding: 0 16px !important;
}

.dash-table >>> tbody td {
  border-bottom: 1px solid var(--card-border) !important;
  padding: 10px 16px !important;
}

.dash-table >>> tbody tr:last-child td {
  border-bottom: none !important;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 600px) {
  .dash-table >>> thead th,
  .dash-table >>> tbody td {
    padding: 8px 10px !important;
    font-size: 12px !important;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Productos/VentaCarrito.vue